/**
 * Copies `text` to the clipboard, returning whether it worked.
 *
 * `navigator.clipboard` requires a secure context (HTTPS/localhost) and is
 * absent over plain HTTP — falls back to the legacy `execCommand('copy')`
 * selection trick, which works either way.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or a non-secure context that still exposes the API —
    // fall through to the legacy path rather than failing outright.
  }

  const previouslyFocused = document.activeElement as HTMLElement | null;
  let area: HTMLTextAreaElement | null = null;
  try {
    area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    // Off-screen but still focusable and rendered: `display: none`, `hidden` or
    // zero opacity make the selection — and therefore the copy — a no-op.
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    // focus() before select() is required: without it the document has no
    // selection for execCommand to act on and an empty string gets copied.
    area.focus();
    area.select();
    area.setSelectionRange(0, text.length);
    const copied = document.execCommand('copy');
    return copied && text.length > 0;
  } catch {
    return false;
  } finally {
    area?.parentNode?.removeChild(area);
    previouslyFocused?.focus?.();
  }
};
